import { db } from '../firebase';
import { collection, addDoc, serverTimestamp, Firestore } from 'firebase/firestore';

export interface ConnectionInfo {
    effectiveType?: string;      // 4g, 3g, 2g, slow-2g
    downlink?: number;           // Mb/s
    rtt?: number;                // Round-trip time in ms
    saveData?: boolean;          // Data saver mode enabled
    type?: string;               // Connection type (wifi, cellular, etc.)
}

export interface ScreenInfo {
    width: number;
    height: number;
    availWidth: number;          // Available width without taskbar
    availHeight: number;         // Available height without taskbar
    colorDepth: number;
    pixelDepth: number;
    orientation?: string;        // portrait-primary, landscape-primary, etc.
    orientationAngle?: number;
}

export interface ViewportInfo {
    innerWidth: number;          // Browser window width
    innerHeight: number;         // Browser window height
    outerWidth: number;          // Full browser width
    outerHeight: number;         // Full browser height
    scrollX: number;
    scrollY: number;
}

export interface PerformanceInfo {
    pageLoadTime?: number;       // Time until page fully loaded
    domContentLoaded?: number;   // Time until DOM ready
    firstPaint?: number;         // First paint timing
    firstContentfulPaint?: number;
    timeToInteractive?: number;
    dnsLookup?: number;
    tcpConnection?: number;
    serverResponse?: number;
    domProcessing?: number;
    resourceLoading?: number;
}

export interface BatteryInfo {
    charging?: boolean;
    level?: number;              // 0-1
    chargingTime?: number;
    dischargingTime?: number;
}

export interface MediaCapabilities {
    pdfViewerEnabled: boolean;
    webglSupported: boolean;
    webglRenderer?: string;
    webglVendor?: string;
    webgl2Supported: boolean;
    canvasSupported: boolean;
    audioContextSupported: boolean;
    webRtcSupported: boolean;
    serviceWorkerSupported: boolean;
    webSocketSupported: boolean;
    localStorageSupported: boolean;
    sessionStorageSupported: boolean;
    indexedDbSupported: boolean;
    cookiesEnabled: boolean;
}

export interface TouchInfo {
    touchSupported: boolean;
    maxTouchPoints: number;
    pointerSupported: boolean;
}

export interface UTMParams {
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_term?: string;
    utm_content?: string;
    utm_id?: string;
    gclid?: string;              // Google Ads Click ID
    fbclid?: string;             // Facebook Click ID
}

export interface LocationInfo {
    path: string;
    fullUrl: string;
    hash: string;
    search: string;
    hostname: string;
    protocol: string;
    port: string;
    origin: string;
}

export interface DocumentInfo {
    title: string;
    characterSet: string;
    contentType: string;
    readyState: string;
    visibilityState: string;
    hidden: boolean;
}

export interface VisitorData {
    // Basic browser info
    userAgent: string;
    language: string;
    languages: readonly string[];
    platform: string;
    vendor: string;
    appName: string;
    appVersion: string;
    product: string;
    productSub: string;
    
    // Privacy settings
    doNotTrack: string | null;
    globalPrivacyControl?: boolean;
    cookiesEnabled: boolean;
    
    // Network/Connection
    onLine: boolean;
    connection?: ConnectionInfo;
    
    // Screen & Display
    screen: ScreenInfo;
    viewport: ViewportInfo;
    pixelRatio: number;
    
    // Time & Location
    timezone: string;
    timezoneOffset: number;      // Minutes from UTC
    localTime: string;           // ISO string of local time
    
    // Hardware
    hardwareConcurrency: number | string;
    deviceMemory: number | string;
    
    // Touch/Input
    touch: TouchInfo;
    
    // Referrer & Navigation
    referrer: string;
    
    // Page/Document info
    location: LocationInfo;
    document: DocumentInfo;
    
    // Marketing
    utm: UTMParams;
    
    // Performance
    performance?: PerformanceInfo;
    
    // Media & Browser capabilities
    mediaCapabilities: MediaCapabilities;
    
    // Battery (if available)
    battery?: BatteryInfo;
    
    // Session info
    sessionId: string;
    isNewVisitor: boolean;
    visitCount: number;
    previousVisitTimestamp?: string;
    
    // Fingerprinting (hashed)
    canvasFingerprint?: string;
    audioFingerprint?: string;
    webglFingerprint?: string;
    
    // Installed plugins count
    pluginsCount: number;
    
    // Fonts count (detected)
    fontsCount?: number;
    
    // IP (server-side)
    ip?: string;
}

export async function trackVisitor(data: VisitorData): Promise<{ success: boolean; id?: string; message?: string }> {
    try {
        // Check if Firebase is initialized
        if (!db) {
            console.error('❌ Firebase not initialized');
            return {
                success: false,
                message: 'Firebase not initialized. Please check your environment variables.',
            };
        }

        const firestoreDb: Firestore = db;
        // Get IP from client (if available via a service or leave as optional)
        const visitorData = {
            ...data,
            timestamp: serverTimestamp(),
        };

        const docRef = await addDoc(collection(firestoreDb, 'visitors'), visitorData);

        console.log('✅ Visitor tracked successfully:', docRef.id);
        return { success: true, id: docRef.id };
    } catch (error) {
        console.error('❌ Error tracking visitor:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        
        // Don't log sensitive Firebase errors in production
        if (import.meta.env.DEV) {
            console.error('Error details:', errorMessage);
        }
        
        return {
            success: false,
            message: 'Internal Server Error',
        };
    }
}

